/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box } from "@mui/material";
import * as maplibregl from "maplibre-gl";
import type { GeoJSONSource } from "maplibre-gl";
import type { FeatureCollection, Point } from "geojson";
import "maplibre-gl/dist/maplibre-gl.css";
import "src/utils/maplibreWorker";
import { useEffect, useImperativeHandle, useRef } from "react";
import { MissionControlTenant } from "src/store/api/generatedApi";
import {
  SOURCE_ID,
  LAYER_GLOW_ID,
  LAYER_CORE_ID,
  DEFAULT_COLOR,
  HOVERED_COLOR,
  DEFAULT_GLOW,
  HOVERED_GLOW,
} from "./millMapConstants";

export interface RegionMapPaneHandle {
  focusTenant: (tenant: MissionControlTenant) => void;
  fitToTenants: (options?: { animate?: boolean; duration?: number }) => void;
}

interface RegionMapPaneProps {
  tenants: MissionControlTenant[];
  highlightedTenantName: string | null;
  selectedTenant: MissionControlTenant | null;
  onSelectTenant: (tenant: MissionControlTenant | null) => void;
  defaultCenter: [number, number];
  defaultZoom: number;
}

const toGeoJson = (
  tenants: MissionControlTenant[]
): FeatureCollection<Point> => ({
  type: "FeatureCollection",
  features: tenants.map((t) => ({
    type: "Feature",
    geometry: { type: "Point", coordinates: [t.longitude, t.latitude] },
    properties: { tenant_name: t.tenant_name },
  })),
});

const fitMapToTenants = (
  map: maplibregl.Map,
  tenants: MissionControlTenant[],
  options: { animate?: boolean; duration?: number; maxZoom?: number } = {}
) => {
  const { animate = true, duration = 1200, maxZoom = 4 } = options;
  if (tenants.length === 0) return;

  if (tenants.length === 1) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const only = tenants[0]!;
    const method = animate ? "flyTo" : "jumpTo";
    map[method]({
      center: [only.longitude, only.latitude],
      zoom: maxZoom,
      duration: animate ? duration : 0,
      essential: true,
    });
    return;
  }

  const bounds = new maplibregl.LngLatBounds();
  tenants.forEach((t) => bounds.extend([t.longitude, t.latitude]));
  map.fitBounds(bounds, {
    padding: 80,
    maxZoom,
    duration: animate ? duration : 0,
    essential: true,
  });
};

export const RegionMapPane = ({
  ref,
  tenants,
  highlightedTenantName,
  selectedTenant,
  onSelectTenant,
  defaultCenter,
  defaultZoom,
}: RegionMapPaneProps & {
  ref?: React.RefObject<RegionMapPaneHandle | null>;
}) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);

  const selectedTenantRef = useRef(selectedTenant);
  selectedTenantRef.current = selectedTenant;

  const tenantsRef = useRef(tenants);
  tenantsRef.current = tenants;

  const onSelectTenantRef = useRef(onSelectTenant);
  onSelectTenantRef.current = onSelectTenant;

  useImperativeHandle(
    ref,
    () => ({
      focusTenant: (tenant: MissionControlTenant) => {
        if (!mapRef.current) return;
        mapRef.current.easeTo({
          center: [tenant.longitude, tenant.latitude],
          zoom: 6,
          duration: 1200,
          offset: [-100, 50],
          essential: true,
          easing: (t) => t * (2 - t),
        });
      },
      fitToTenants: (options) => {
        if (!mapRef.current) return;
        fitMapToTenants(mapRef.current, tenantsRef.current, options);
      },
    }),
    []
  );

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
      center: defaultCenter,
      zoom: defaultZoom,
      pitch: 0,
      bearing: 0,
      attributionControl: false,
    });

    map.scrollZoom.disable();
    map.boxZoom.disable();
    map.doubleClickZoom.disable();
    map.dragRotate.disable();
    if (map.dragPan && typeof map.dragPan.disable === "function")
      map.dragPan.disable();
    if (map.keyboard && typeof map.keyboard.disable === "function")
      map.keyboard.disable();
    if (
      map.touchZoomRotate &&
      typeof map.touchZoomRotate.disable === "function"
    )
      map.touchZoomRotate.disable();

    map.on("load", () => {
      map.addSource(SOURCE_ID, {
        type: "geojson",
        data: toGeoJson(tenantsRef.current),
      });

      map.addLayer({
        id: LAYER_GLOW_ID,
        type: "circle",
        source: SOURCE_ID,
        paint: {
          "circle-color": DEFAULT_GLOW,
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 2, 10, 6, 32],
          "circle-blur": 0.6,
        },
      });

      map.addLayer({
        id: LAYER_CORE_ID,
        type: "circle",
        source: SOURCE_ID,
        paint: {
          "circle-color": DEFAULT_COLOR,
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 2, 5, 6, 10],
          "circle-stroke-color": "#111",
          "circle-stroke-width": 2,
        },
      });

      map.getCanvas().style.cursor = "default";

      map.on("mouseenter", LAYER_CORE_ID, (e) => {
        map.getCanvas().style.cursor = "pointer";
        const feature = e.features?.[0];
        const name = feature?.properties?.tenant_name as string | undefined;
        if (name) {
          map.setPaintProperty(LAYER_CORE_ID, "circle-color", [
            "match",
            ["get", "tenant_name"],
            name,
            HOVERED_COLOR,
            DEFAULT_COLOR,
          ]);
          map.setPaintProperty(LAYER_GLOW_ID, "circle-color", [
            "match",
            ["get", "tenant_name"],
            name,
            HOVERED_GLOW,
            DEFAULT_GLOW,
          ]);
        }
      });

      map.on("mouseleave", LAYER_CORE_ID, () => {
        map.getCanvas().style.cursor = "default";
        const selected = selectedTenantRef.current;
        if (selected) {
          map.setPaintProperty(LAYER_CORE_ID, "circle-color", [
            "match",
            ["get", "tenant_name"],
            selected.tenant_name,
            HOVERED_COLOR,
            DEFAULT_COLOR,
          ]);
          map.setPaintProperty(LAYER_GLOW_ID, "circle-color", [
            "match",
            ["get", "tenant_name"],
            selected.tenant_name,
            HOVERED_GLOW,
            DEFAULT_GLOW,
          ]);
        } else {
          map.setPaintProperty(LAYER_CORE_ID, "circle-color", DEFAULT_COLOR);
          map.setPaintProperty(LAYER_GLOW_ID, "circle-color", DEFAULT_GLOW);
        }
      });

      map.on("click", (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: [LAYER_CORE_ID],
        });
        if (features && features.length > 0) {
          const first = features[0];
          const properties = (first?.properties ?? {}) as {
            tenant_name?: string;
          };
          const name =
            typeof properties.tenant_name === "string"
              ? properties.tenant_name
              : undefined;
          if (name) {
            const found = tenantsRef.current.find(
              (t) => t.tenant_name === name
            );
            if (found) onSelectTenantRef.current(found);
          }
        } else {
          onSelectTenantRef.current(null);
        }
      });

      map.resize();
      requestAnimationFrame(() => {
        map.triggerRepaint();
      });
      void map.once("idle", () => {
        fitMapToTenants(map, tenantsRef.current, { animate: false });
      });
    });

    mapRef.current = map;

    let ro: ResizeObserver | undefined;
    if (mapContainerRef.current && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => map.resize());
      ro.observe(mapContainerRef.current);
    }

    return () => {
      ro?.disconnect();
      if (map.getLayer(LAYER_CORE_ID)) map.removeLayer(LAYER_CORE_ID);
      if (map.getLayer(LAYER_GLOW_ID)) map.removeLayer(LAYER_GLOW_ID);
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- mount-only: map teardown/re-creation on prop change would destroy user state
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.resize();
    const src = mapRef.current.getSource<GeoJSONSource>(SOURCE_ID);
    if (src) {
      void src.setData(toGeoJson(tenants));
      fitMapToTenants(mapRef.current, tenants, { duration: 600 });
    }
  }, [tenants]);

  useEffect(() => {
    if (!mapRef.current?.isStyleLoaded()) return;
    const map = mapRef.current;

    if (highlightedTenantName) {
      map.setPaintProperty(LAYER_CORE_ID, "circle-color", [
        "match",
        ["get", "tenant_name"],
        highlightedTenantName,
        HOVERED_COLOR,
        DEFAULT_COLOR,
      ]);
      map.setPaintProperty(LAYER_GLOW_ID, "circle-color", [
        "match",
        ["get", "tenant_name"],
        highlightedTenantName,
        HOVERED_GLOW,
        DEFAULT_GLOW,
      ]);
    } else {
      map.setPaintProperty(LAYER_CORE_ID, "circle-color", DEFAULT_COLOR);
      map.setPaintProperty(LAYER_GLOW_ID, "circle-color", DEFAULT_GLOW);
    }
  }, [highlightedTenantName]);

  return (
    <Box
      ref={mapContainerRef}
      data-testid="mill-map-region-pane"
      sx={{ height: "100%", width: "100%" }}
    />
  );
};

RegionMapPane.displayName = "RegionMapPane";
